import { Res, Result } from '../result';
import { NormalizeOpt } from './normalize.types';
import { ArrayWithObjectKeys, ObjectOrArray } from '../arrays/arrays.types';
import _, { curryRight } from 'lodash';
import { arrayUtils } from '../arrays/arrays.utils';

const runTypeCheckers = <TOut>(
  input: any,
  checkers: NormalizeOpt<TOut, any>[]
) => {
  const chekersByName = arrayUtils.toObjectWithKeyPosition(checkers, 0, 'func');
  const matchedTypes = checkers
    .map((x) => [x[0], x[1](input)] as [string, boolean])
    .filter((x) => x[1]);
  if (matchedTypes.length === 1) {
    const normalizer = chekersByName(matchedTypes[0][0])[2];
    return Res.Ok(normalizer(input));
  }
  if (matchedTypes.length > 1) {
    return Res.Error(
      'More than one type was found that matches provided input.'
    );
  }
  return Res.Error('No types where matched.');
};

const createCurriedNormalizer = <TOut, TIn = any>(
  checkers: NormalizeOpt<TOut, TIn>[]
): ((input: TIn) => Result<TOut>) => {
  return curryRight(runTypeCheckers)(checkers);
};

const normalizeObjOrArray = <T>(
  objOrArr: ObjectOrArray<T>,
  positionMap: ArrayWithObjectKeys<T>
): T => {
  return _.isArray(objOrArr)
    ? arrayUtils.toObjectByProps(objOrArr, positionMap)
    : objOrArr;
};

export const normalizeObjOrArrayCurried = <T>(props: ArrayWithObjectKeys<T>) =>
  curryRight<ObjectOrArray<T>, ArrayWithObjectKeys<T>, T>(normalizeObjOrArray)(
    props
  );

export interface NormalizeUtils {
  checkTypes: typeof runTypeCheckers;
  run: typeof runTypeCheckers;
  create: typeof createCurriedNormalizer;
  objOrArray: typeof normalizeObjOrArray;
  objOrArrayCurried: typeof normalizeObjOrArrayCurried;
}

const normalizeUtils = (): NormalizeUtils => {
  return {
    checkTypes: runTypeCheckers,
    run: runTypeCheckers,
    create: createCurriedNormalizer,
    objOrArray: normalizeObjOrArray,
    objOrArrayCurried: normalizeObjOrArrayCurried,
  };
};

export const normalize = normalizeUtils();
